import {
  DialogContent,
  Dialog,
  DialogProps,
  useEventCallback,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/styles';
import { memo, useState } from 'react';
import NewPerson from './form';
import { Accordion, AccordionDetails, Typography } from '@mui/material';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { ExpandMore } from '@mui/icons-material';
import {
  RegistrationBewareFormVM,
  RegistrationNewPersonVM,
} from 'mutation/view-models/registration-beware';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import { DateTime } from 'luxon';

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

interface ModalNewPersonProps {
  open: boolean;
  onClose: (open: boolean) => void;
}

const ModalNewPersonComponent = ({ open, onClose }: ModalNewPersonProps): React.ReactElement => {
  const [openAccordion, setOpenAccordion] = useState(true);

  const formContext = useFormikContext<RegistrationBewareFormVM>();

  const onSubmit = useEventCallback(() => {
    if (formContext.errors?.registrationNewPerson) {
      const touched = {
        registrationNewPerson: {
          address: true,
          birthChoiceDate: true,
          birthDate: true,
          fiscalCode: true,
          foreignBirth: { foreignState: true, foreignMunicipality: true },
          foreignDomicile: { foreignState: true, foreignMunicipality: true },
          gender: true,
          isForeignBirth: true,
          isForeignDomicile: true,
          italianBirth: { municipality: true },
          italianDomicile: { municipality: true },
          name: true,
          surname: true,
          typology: true,
        },
      } as unknown as FormikTouched<RegistrationBewareFormVM>;

      formContext?.setTouched(touched);

      return;
    }

    formContext.setValues({
      changeControlDataForm: {
        effectiveDate: DateTime.local(),
        validationFiscalCode: '',
      },
      subjectAttributes: {
        disabled: false,
      },
      person: {
        fiscalCode: formContext.values?.registrationNewPerson?.fiscalCode || '',
        name: formContext.values?.registrationNewPerson?.name || '',
        surname: formContext.values?.registrationNewPerson?.surname || '',
      },
      registrationNewPerson: {
        ...formContext.values?.registrationNewPerson,
      },
    });
  });

  const setValues = useEventCallback((values: RegistrationNewPersonVM) => {
    formContext.setFieldValue('registrationNewPerson', {
      ...(formContext.values.registrationNewPerson || {}),
      ...values,
    });
  });

  const setFieldValue = useEventCallback(
    (
      key: keyof RegistrationNewPersonVM,
      value: RegistrationNewPersonVM[keyof RegistrationNewPersonVM]
    ) => {
      formContext.setFieldValue('registrationNewPerson', {
        ...(formContext.values.registrationNewPerson || {}),
        [key]: value,
      });
    }
  );

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="alert-dialog-title"
      fullScreen={fullScreen}
      aria-describedby="alert-dialog-description"
    >
      <DialogContent style={{ padding: '30px 24px' }}>
        <Accordion
          expanded={openAccordion}
          onChange={() => {
            setOpenAccordion(!openAccordion);
          }}
        >
          <AccordionSummary
            aria-controls="panel-modal-content"
            id="panel-modal-header"
            expandIcon={<ExpandMore />}
          >
            <Typography>Inserimento persona per diffida</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <NewPerson
              values={formContext.values?.registrationNewPerson}
              setValues={setValues}
              setFieldValue={setFieldValue}
              onSubmit={onSubmit}
              errors={
                formContext.errors?.registrationNewPerson as FormikErrors<RegistrationNewPersonVM>
              }
              touched={
                formContext.touched
                  ?.registrationNewPerson as unknown as FormikTouched<RegistrationNewPersonVM>
              }
            />
          </AccordionDetails>
        </Accordion>
      </DialogContent>
    </PmiDialog>
  );
};

ModalNewPersonComponent.displayName = 'Modal new person';

const ModalNewPerson = memo(ModalNewPersonComponent);

export default ModalNewPerson;
